import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Handshake, Headset, Database } from 'phosphor-react';
import workstationImageSmall from '../assets/workstation-small.jpg';
import workstationImageSm from '../assets/workstation-sm.webp';
import workstationImageMd from '../assets/workstation-md.webp';
import workstationImageLg from '../assets/workstation-lg.webp';
import workstationImageSmJpg from '../assets/workstation-sm.jpg';
import workstationImageMdJpg from '../assets/workstation-md.jpg';
import workstationImageLgJpg from '../assets/workstation-lg.jpg';
import TestimonialCarousel from '../components/TestimonialCarousel';
import Seo from '../components/Seo';

// Feature Card Component
const FeatureCard = ({ title, description, icon: Icon }) => (
  <div className="bg-white rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 h-full">
    <div className="flex items-center gap-3 mb-4">
      {Icon && <Icon size={32} weight="regular" className="text-terrapin-blue" />}
      <h3 className="text-xl font-semibold text-terrapin-blue">{title}</h3>
    </div>
    <p className="text-gray-600 mb-4">{description}</p>
    <Link
      to="/services"
      className="inline-flex items-center text-terrapin-blue hover:text-terrapin-red transition-colors duration-200"
    >
      Learn More
      <svg className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  </div>
);

// Hero Background Component
const HeroBackground = () => {
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    // Preload the largest image
    const img = new Image();
    img.src = workstationImageLg;
    img.onload = () => setImageLoaded(true);
  }, []);

  return (
    <div className="absolute inset-0 z-0">
      {/* Blur-up small image */}
      <img
        src={workstationImageSmall}
        alt=""
        className={`w-full h-full object-cover brightness-50 filter blur-sm scale-105 transition-opacity duration-300 ${imageLoaded ? 'opacity-0' : 'opacity-100'
          }`}
        aria-hidden="true"
      />

      {/* Main image with WebP support and responsive sizes */}
      <picture>
        <source
          srcSet={`
            ${workstationImageSm} 640w,
            ${workstationImageMd} 1024w,
            ${workstationImageLg} 1920w
          `}
          sizes="100vw"
          type="image/webp"
        />
        <source
          srcSet={`
            ${workstationImageSmJpg} 640w,
            ${workstationImageMdJpg} 1024w,
            ${workstationImageLgJpg} 1920w
          `}
          sizes="100vw"
          type="image/jpeg"
        />
        <img
          src={workstationImageLgJpg}
          alt="Modern workstation showcasing Terrapin Networks' professional IT environment"
          className={`absolute inset-0 w-full h-full object-cover brightness-50 filter transition-opacity duration-700 ${imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          loading="eager"
        />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/90 to-black/85"></div>
    </div>
  );
};

// Hero Section Component
const HeroSection = () => (
  <section className="relative h-screen flex flex-col items-center justify-center overflow-hidden">
    <HeroBackground />

    {/* Hero Content */}
    <div className="relative z-10 text-center text-white px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto pb-48">
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
      A Technology Team for Your Small Business
      </h1>

      <p className="text-lg sm:text-xl mb-8 text-gray-200 max-w-3xl mx-auto">
      Smart, secure, and human support for your company's tech system — without long-term contracts or big-business complexity</p>

      <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
        <Link
          to="/contact"
          className="btn-base btn-primary min-w-[200px]"
        >
          Book a Call
        </Link>
        <Link
          to="/services"
          className="btn-base btn-ghost min-w-[200px]"
        >
          See What We Do
        </Link>
      </div>
    </div>

    {/* Testimonials — desktop only, full-width band in the lower portion of the hero */}
    <div className="hidden md:block absolute bottom-0 left-0 right-0 z-10 bg-white bg-opacity-10 pb-16">
      <TestimonialCarousel theme="dark" />
    </div>

    {/* Scroll Indicator */}
    <div className="hidden md:block absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20">
      <a
        href="#copy-section"
        className="p-4 bg-white/10 backdrop-blur-sm rounded-full cursor-pointer hover:bg-white/20 transition-colors duration-200"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('copy-section').scrollIntoView({ behavior: 'smooth' });
        }}
      >
        <svg
          className="w-6 h-6 text-white animate-bounce"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </a>
    </div>
  </section>
);

const Home = () => (
  <div className="min-h-screen bg-gray-50">
    <Seo
      title="Managed IT for Small Business | Terrapin Networks"
      description="Terrapin Networks is your local MSP in Northern Michigan, delivering managed IT, cybersecurity, and helpdesk support to small businesses."
    />
    {/* Hero Section */}
    <HeroSection />

    {/* Copy Section */}
    <section id="copy-section" className="relative bg-gray-50 py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8 lg:p-12 text-center">
          <p className="text-lg text-gray-600 mb-8">
            If you've got 5–20 people working on computers every day, you've probably run into one of two problems: You're too small for the big providers to take seriously, or too big to keep relying on a solo tech guy. That's where we come in.
          </p>
          
          <p className="text-xl text-terrapin-blue font-medium mb-6">
            Terrapin Networks is a tech team for small companies.
          </p>
          
          <p className="text-lg text-gray-600 mb-8">
            We don't just fix things when they break; we design, manage, and maintain your whole system like it's our own. Your team gets responsive support from people they know, you get peace of mind knowing your tech is covered. We've been at this since the early 90s. No long-term contracts, no upsells, just steady, experienced support from a team that gets it, and gets to know you.
          </p>
          
          <Link
            to="/services"
            className="btn-base btn-primary min-w-[200px]"
          >
            Our Services
          </Link>
        </div>
      </div>
    </section>

    {/* Features Section */}
    {/* <section className="relative bg-gray-100 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -translate-y-32">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </div>
      </div>
    </section> */}
  </div>
);

export default Home;
